"use client";

import { useEffect, useState, useCallback } from "react";
import { ResponsiveModal, ModalContainer, MODAL_IDS } from "@wtc/ui";
import { useDetailActionContextOptional } from "@/contexts/DetailActionContext";
import type { ResearchModalContextData } from "./research-company/types";
import { useResearchCompanyModal } from "./research-company/useResearchCompanyModal";
import { SearchStep } from "./research-company/SearchStep";
import { CompanySelectionStep } from "./research-company/CompanySelectionStep";
import { ContactSelectionStep } from "./research-company/ContactSelectionStep";
import { ImportResultsStep } from "./research-company/ImportResultsStep";
import { NoCompanyStep } from "./research-company/NoCompanyStep";
import { resolveCompanyModalContext } from "./modal-company-context";
import type { ContextContactFallback } from "./modal-company-context";

interface ResearchCompanyModalProps {
  contextContact?: ContextContactFallback;
}

export function ResearchCompanyModal({
  contextContact,
}: ResearchCompanyModalProps) {
  return (
    <ModalContainer<ResearchModalContextData> modalId={MODAL_IDS.companies.research}>
      {({ isOpen, data, onClose }) => (
        <ResearchCompanyModalContent
          isOpen={isOpen}
          data={data}
          onClose={onClose}
          contextContact={contextContact}
        />
      )}
    </ModalContainer>
  );
}

interface ResearchCompanyModalContentProps {
  isOpen: boolean;
  data?: ResearchModalContextData;
  onClose: () => void;
  contextContact?: ContextContactFallback;
}

function ResearchCompanyModalContent({
  isOpen,
  data,
  onClose,
  contextContact,
}: ResearchCompanyModalContentProps) {
  const detailContext = useDetailActionContextOptional();
  const [linkedCompanyId, setLinkedCompanyId] = useState<string | null>(null);

  const handleCloseModal = useCallback(() => {
    onClose();
    setLinkedCompanyId(null);
  }, [onClose]);

  const modalState = useResearchCompanyModal({
    linkedCompanyId,
    onClose: handleCloseModal,
  });

  const pageCompanyId = detailContext?.entityIds?.companyId || null;

  const {
    step,
    setStep,
    searchQuery,
    setSearchQuery,
    candidates,
    selectedCandidate,
    contacts,
    selectedContacts,
    importResults,
    loading,
    error,
    performSearch,
    handleSearch,
    handleSelectCompany,
    toggleContactSelection,
    toggleSelectAll,
    handleImportContacts,
    handleClose,
  } = modalState;

  // Initialize modal state when opened
  useEffect(() => {
    if (!isOpen) return;

    const { contactData, companyName } = resolveCompanyModalContext({
      modalContact: data?.contact,
      modalCompany: data?.company,
      contextContact,
      detailItem: detailContext?.item,
      detailEntityName: detailContext?.pageConfig?.entityName,
    });

    if (pageCompanyId) {
      setLinkedCompanyId(pageCompanyId);
      if (contactData?.company_name) {
        setSearchQuery(contactData.company_name);
        setStep("search");
        setTimeout(() => performSearch(contactData.company_name || ""), 0);
      } else {
        setStep("search");
      }
    } else if (contactData?.company_id) {
      setLinkedCompanyId(contactData.company_id);
      if (contactData.company_name) {
        setSearchQuery(contactData.company_name);
        setStep("search");
        setTimeout(() => performSearch(contactData.company_name || ""), 0);
      } else {
        setStep("search");
      }
    } else if (contactData && !contactData.company_id) {
      setStep("no-company");
      setLinkedCompanyId(null);
    } else if (companyName) {
      setSearchQuery(companyName);
      setStep("search");
      setTimeout(() => performSearch(companyName), 0);
    } else {
      setStep("search");
      setLinkedCompanyId(null);
    }
  }, [
    isOpen,
    data,
    contextContact,
    detailContext?.item,
    detailContext?.pageConfig?.entityName,
    pageCompanyId,
    performSearch,
    setSearchQuery,
    setStep,
  ]);

  // Auto-close when no company linked
  useEffect(() => {
    if (isOpen && step === "no-company" && linkedCompanyId === null) {
      handleClose();
    }
  }, [isOpen, step, linkedCompanyId, handleClose]);

  return (
    <ResponsiveModal
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) handleClose();
      }}
      title="Research Company"
      globalModalId={MODAL_IDS.companies.research}
    >
      <div className="space-y-4">
        {step === "no-company" && (
          <NoCompanyStep
            onClose={handleClose}
            onSearchForCompany={() => setStep("search")}
          />
        )}

        {step === "search" && (
          <SearchStep
            searchQuery={searchQuery}
            onSearchQueryChange={setSearchQuery}
            onSearch={handleSearch}
            loading={loading}
            error={error}
          />
        )}

        {step === "company-selection" && (
          <CompanySelectionStep
            candidates={candidates}
            onSelectCompany={handleSelectCompany}
            onBack={() => setStep("search")}
            loading={loading}
          />
        )}

        {step === "contact-selection" && (
          <ContactSelectionStep
            companyName={selectedCandidate?.name ?? "Unknown"}
            contacts={contacts}
            selectedContacts={selectedContacts}
            onToggleContact={toggleContactSelection}
            onToggleSelectAll={toggleSelectAll}
            onImport={handleImportContacts}
            onBack={() => setStep("company-selection")}
            loading={loading}
            error={error}
          />
        )}

        {step === "import-results" && (
          <ImportResultsStep results={importResults} onDone={handleClose} />
        )}
      </div>
    </ResponsiveModal>
  );
}
